import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import BrandLogoMatcher from '../components/BrandLogoMatcher';
import { ProductSkeletonGrid } from '../components/LoadingSkeletons/ProductSkeleton';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ITEMS_PER_PAGE = 12;

const Tuning = () => {
  const { t } = useLanguage();
  const { addToCart } = useCart();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [lastSearchedQuery, setLastSearchedQuery] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [products, setProducts] = useState([]);
  const [favouriteProducts, setFavouriteProducts] = useState([]);
  const [displayedFavourites, setDisplayedFavourites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [favouritesLoading, setFavouritesLoading] = useState(false);
  const [favouritePage, setFavouritePage] = useState(1);
  const [hasMoreFavourites, setHasMoreFavourites] = useState(true);
  const [sortBy, setSortBy] = useState('name_asc');
  const loaderRef = useRef(null);
  const favouritesLoadingRef = useRef(false);

  const handleAddToCart = async (productId) => {
    try {
      await addToCart(productId, 1);
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const fetchFavouritesPage = useCallback(async (pageToLoad = 1, reset = false) => {
    if (favouritesLoadingRef.current) return;

    const skip = (pageToLoad - 1) * ITEMS_PER_PAGE;

    favouritesLoadingRef.current = true;
    setFavouritesLoading(true);

    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        skip: String(skip),
        limit: String(ITEMS_PER_PAGE),
      });

      const response = await fetch(`${API_URL}/api/tuning/favourites?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch favourites');
      }

      const data = await response.json();

      if (reset) {
        setFavouriteProducts(data);
        setDisplayedFavourites(data);
        setFavouritePage(1);
      } else {
        setFavouriteProducts((prev) => [...prev, ...data]);
        setDisplayedFavourites((prev) => [...prev, ...data]);
        setFavouritePage(pageToLoad);
      }

      setHasMoreFavourites(data.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching favourite tuning products:', error);
      if (reset) {
        setFavouriteProducts([]);
        setDisplayedFavourites([]);
      }
      setHasMoreFavourites(false);
    } finally {
      favouritesLoadingRef.current = false;
      setFavouritesLoading(false);
      if (reset) {
        setPageLoading(false);
      }
    }
  }, [sortBy]);

  // Fetch favourite ECU tuning products on mount
  useEffect(() => {
    setFavouriteProducts([]);
    setDisplayedFavourites([]);
    setFavouritePage(1);
    setHasMoreFavourites(true);
    setPageLoading(true);
    fetchFavouritesPage(1, true);
  }, [fetchFavouritesPage]);

  const loadMoreFavourites = useCallback(() => {
    if (favouritesLoading || !hasMoreFavourites) return;
    const nextPage = favouritePage + 1;
    fetchFavouritesPage(nextPage, false);
  }, [favouritesLoading, hasMoreFavourites, favouritePage, fetchFavouritesPage]);

  // Infinite scroll handler for favourites
  useEffect(() => {
    const currentLoader = loaderRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreFavourites && !pageLoading && !hasSearched) {
          loadMoreFavourites();
        }
      },
      { threshold: 0.1 }
    );

    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMoreFavourites, pageLoading, hasSearched, loadMoreFavourites]);

  // Fetch products when search query changes
  useEffect(() => {
    const search = searchParams.get('search');
    if (search) {
      setSearchQuery(search);
      searchProducts(search);
    }
  }, [searchParams]);

  // Fetch manufacturers for dropdowns
  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/tuning/manufacturers`);
        if (response.ok) {
          const data = await response.json();
          setManufacturers(data);
        }
      } catch (error) {
        console.error('Error fetching manufacturers:', error);
      }
    };
    fetchManufacturers();
  }, []);

  // Update models when manufacturer changes
  useEffect(() => {
    if (selectedManufacturer) {
      const fetchModels = async () => {
        try {
          const response = await fetch(`${API_URL}/api/tuning/models?manufacturer=${encodeURIComponent(selectedManufacturer)}`);
          if (response.ok) {
            const data = await response.json();
            setModels(data);
          }
        } catch (error) {
          console.error('Error fetching models:', error);
        }
      };
      fetchModels();
    } else {
      setModels([]);
    }
  }, [selectedManufacturer]);

  // Update years when model changes
  useEffect(() => {
    if (selectedModel && selectedManufacturer) {
      const fetchYears = async () => {
        try {
          const params = new URLSearchParams({
            manufacturer: selectedManufacturer,
            model: selectedModel
          });
          const response = await fetch(`${API_URL}/api/tuning/years?${params}`);
          if (response.ok) {
            const data = await response.json();
            // data is already an array of year ranges like ["2021-2024", "2019-2020", etc.]
            // Expand ranges to individual years for the dropdown
            const yearsSet = new Set();
            data.forEach(yearRange => {
              const yearRangeMatch = yearRange.match(/(\d{4})(?:-(\d{4})|\+)?/);
              if (yearRangeMatch) {
                const startYear = parseInt(yearRangeMatch[1]);
                const endYear = yearRangeMatch[2] ? parseInt(yearRangeMatch[2]) : startYear;
                const hasPlus = yearRange.includes('+');
                
                if (hasPlus) {
                  // For "2020+" add current year range
                  const currentYear = new Date().getFullYear();
                  for (let year = startYear; year <= currentYear; year++) {
                    yearsSet.add(year);
                  }
                } else {
                  // Add all years in the range
                  for (let year = startYear; year <= endYear; year++) {
                    yearsSet.add(year);
                  }
                }
              }
            });
            setYears(Array.from(yearsSet).sort((a, b) => b - a)); // Sort descending
          }
        } catch (error) {
          console.error('Error fetching years:', error);
        }
      };
      fetchYears();
    } else {
      setYears([]);
    }
  }, [selectedModel, selectedManufacturer]);

  // Trigger search when dropdown selections change
  useEffect(() => {
    if (selectedManufacturer || selectedModel || selectedYear) {
      searchProducts(searchQuery);
    }
  }, [selectedManufacturer, selectedModel, selectedYear]);

  // Re-run search when sort option changes
  useEffect(() => {
    if (hasSearched) {
      searchProducts(searchQuery);
    }
  }, [sortBy]);

  const searchProducts = async (query) => {
    // Allow search with dropdowns even if query is empty
    if (!query && !selectedManufacturer && !selectedModel && !selectedYear) {
      setHasSearched(false);
      return;
    }

    if (query && query.trim().length < 2) {
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    
    // Build display query from dropdowns if text search is empty
    let displayQuery = query || '';
    if (!query && (selectedManufacturer || selectedModel || selectedYear)) {
      const parts = [];
      if (selectedManufacturer) parts.push(selectedManufacturer);
      if (selectedModel) parts.push(selectedModel);
      if (selectedYear) parts.push(selectedYear);
      displayQuery = parts.join(' ');
    }
    setLastSearchedQuery(displayQuery);

    try {
      let searchQuery = query || '';
      
      // Extract year from text query if present
      const yearMatch = searchQuery.match(/\b(19\d{2}|20\d{2})\b/);
      const extractedYear = yearMatch ? parseInt(yearMatch[0]) : null;
      
      // Remove year from query for backend search
      if (yearMatch) {
        searchQuery = searchQuery.replace(/\s*\b19\d{2}|20\d{2}\b\s*/, '').trim();
      }
      
      console.log('Search params:', { 
        originalQuery: query, 
        searchQuery, 
        extractedYear,
        dropdowns: { selectedManufacturer, selectedModel, selectedYear }
      });
      
      // Build API URL with query parameters
      const params = new URLSearchParams();
      params.append('limit', '1000');
      params.append('sort_by', sortBy);
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      if (selectedManufacturer) {
        params.append('manufacturer', selectedManufacturer);
      }
      
      if (selectedModel) {
        params.append('model', selectedModel);
      }
      
      // Use dropdown year if selected, otherwise use extracted year from text
      const yearToFilter = selectedYear ? parseInt(selectedYear) : extractedYear;
      if (yearToFilter) {
        params.append('year', yearToFilter);
      }
      
      const apiUrl = `${API_URL}/api/tuning/?${params}`;
      
      const response = await fetch(apiUrl);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      window.history.pushState({}, '', `/tuning?search=${encodeURIComponent(searchQuery)}`);
      searchProducts(searchQuery);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setSelectedManufacturer('');
    setSelectedModel('');
    setSelectedYear('');
    setProducts([]);
    setHasSearched(false);
    window.history.pushState({}, '', '/tuning');
  };

  return (
    <>
      <SEO
        title={t('seo.tuning.title')}
        description={t('seo.tuning.description')}
        keywords={t('seo.tuning.keywords')}
        type="website"
      />

      {/* Search Hero Section */}
      <section className="relative bg-gradient-to-br from-racing-black via-racing-darkGray to-gray-900 text-white py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              {t('home.searchYourBike')}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Search by brand, model, and year to find compatible tuning solutions
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="relative">
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Clear dropdowns when typing
                    if (e.target.value) {
                      setSelectedManufacturer('');
                      setSelectedModel('');
                      setSelectedYear('');
                    }
                  }}
                  placeholder={t('home.searchPlaceholder')}
                  className="flex-1 px-6 py-4 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-racing-red disabled:bg-gray-300 disabled:cursor-not-allowed"
                  minLength={2}
                  disabled={selectedManufacturer || selectedModel || selectedYear}
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-racing-red hover:bg-red-700 rounded-lg font-semibold text-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={selectedManufacturer || selectedModel || selectedYear}
                >
                  {t('home.searchButton')}
                </button>
              </div>
              
              {/* Dropdown Filters */}
              <div className="mt-4 flex flex-col md:flex-row gap-4">
                <select
                  value={selectedManufacturer}
                  onChange={(e) => {
                    setSelectedManufacturer(e.target.value);
                    setSelectedModel('');
                    setSelectedYear('');
                    // Clear search query when using dropdowns
                    if (e.target.value) {
                      setSearchQuery('');
                    }
                  }}
                  disabled={searchQuery.length > 0}
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-racing-red disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <option value="">{t('home.selectManufacturer')}</option>
                  {manufacturers.map(manufacturer => (
                    <option key={manufacturer} value={manufacturer}>{manufacturer}</option>
                  ))}
                </select>
                
                <select
                  value={selectedModel}
                  onChange={(e) => {
                    setSelectedModel(e.target.value);
                    setSelectedYear('');
                  }}
                  disabled={!selectedManufacturer || searchQuery.length > 0}
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-racing-red disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <option value="">{t('home.selectModel')}</option>
                  {models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
                
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={!selectedModel || searchQuery.length > 0}
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-racing-red disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <option value="">{t('home.selectYear')}</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              {/* Clear Button */}
              {(searchQuery || selectedManufacturer || selectedModel || selectedYear || hasSearched) && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={handleClear}
                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Clear All
                  </button>
                </div>
              )}
              
              {searchQuery.length > 0 && searchQuery.length < 2 && (
                <p className="text-sm text-yellow-400 mt-2">
                  {t('home.searchMinChars')}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      {/* Favourites Section - Hidden when search is active */}
      {!pageLoading && !hasSearched && favouriteProducts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <div className="mb-8">
              <motion.h2
                className="text-3xl font-display font-bold mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                ⭐ {t('products.favourite')}
              </motion.h2>

              <motion.div
                className="flex items-center gap-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <label className="font-semibold text-gray-700">
                  {t('home.sortBy')}:
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red cursor-pointer"
                >
                  <option value="name_asc">{t('home.sortNameAZ')}</option>
                  <option value="name_desc">{t('home.sortNameZA')}</option>
                </select>
              </motion.div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayedFavourites.map((product, index) => (
                <motion.div
                  key={product.id}
                  className="card flex flex-col h-full hover:shadow-lg transition-shadow relative"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  {/* Favourite Badge */}
                  <div className="absolute top-3 left-3 bg-racing-red text-white px-3 py-1 rounded-full text-xs font-bold z-10">
                    Favourite
                  </div>

                  <Link to={`/tuning/${product.slug}`} state={{ fromTuning: true }} className="flex flex-col h-full">
                    {/* Manufacturer Logo - Centered */}
                    <div className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center p-8 overflow-hidden">
                      {product.manufacturer && (
                        <BrandLogoMatcher 
                          manufacturer={product.manufacturer} 
                          alt={`${product.manufacturer} logo`}
                          className="max-w-full max-h-full object-contain"
                        />
                      )}
                    </div>
                    
                    {/* Product Info */}
                    <div className="pt-3 px-4 pb-4 flex flex-col flex-grow">
                      <h3 className="font-bold text-base leading-tight hover:text-racing-red transition text-center">
                        {product.name}
                      </h3>
                      <p className="text-gray-600 text-xs text-center mt-1">
                        {t('products.ecuTuningSolution')}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Infinite Scroll Loader */}
            {hasMoreFavourites && (
              <div 
                ref={loaderRef}
                className="mt-12"
              >
                <div className="h-10 bg-gray-200 rounded w-1/4 mb-8 animate-pulse"></div>
                <ProductSkeletonGrid count={4} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Search Results Section */}
      {hasSearched && (
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            {loading ? (
              <>
                <div className="h-10 bg-gray-200 rounded w-1/2 mb-8 animate-pulse"></div>
                <ProductSkeletonGrid count={8} />
              </>
            ) : (
              <>
                <div className="mb-8">
                  <motion.h2
                    className="text-3xl font-display font-bold mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {products.length > 0
                      ? `${t('products.searchResultsFor')} "${lastSearchedQuery}"`
                      : t('products.noProductsFound')}
                  </motion.h2>

                  {products.length > 0 && (
                    <motion.div
                      className="flex items-center gap-4"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                    >
                      <label className="font-semibold text-gray-700">
                        {t('home.sortBy')}:
                      </label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red cursor-pointer"
                      >
                        <option value="name_asc">{t('home.sortNameAZ')}</option>
                        <option value="name_desc">{t('home.sortNameZA')}</option>
                      </select>
                    </motion.div>
                  )}
                </div>

                {products.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {products.map((product, index) => (
                      <motion.div
                        key={product.id}
                        className="card flex flex-col h-full hover:shadow-lg transition-shadow"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <Link to={`/tuning/${product.slug}`} state={{ fromTuning: true }} className="flex flex-col h-full">
                          {/* Manufacturer Logo - Centered */}
                          <div className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center p-8 overflow-hidden">
                            {product.manufacturer && (
                              <BrandLogoMatcher 
                                manufacturer={product.manufacturer} 
                                alt={`${product.manufacturer} logo`}
                                className="max-w-full max-h-full object-contain"
                              />
                            )}
                          </div>
                          
                          {/* Product Info */}
                          <div className="pt-3 px-4 pb-4 flex flex-col flex-grow">
                            <h3 className="font-bold text-base leading-tight hover:text-racing-red transition text-center">
                              {product.name}
                            </h3>
                            <p className="text-gray-600 text-xs text-center mt-1">
                              {t('products.ecuTuningSolution')}
                            </p>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-xl text-gray-600 mb-4">
                      {t('products.noProductsFound')}
                    </p>
                    <p className="text-gray-500 mb-6">
                      {t('products.tryDifferentKeywords')}
                    </p>
                    <Link
                      to="/contact"
                      className="inline-block px-6 py-3 bg-racing-red text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                    >
                      {t('nav.contact')}
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Page Loading State */}
      {pageLoading && (
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <div className="h-10 bg-gray-200 rounded w-1/4 mb-8 animate-pulse"></div>
            <ProductSkeletonGrid count={8} />
          </div>
        </section>
      )}
    </>
  );
};

export default Tuning;
